import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, CircularProgress, InputAdornment, MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { Search as SearchIcon, Visibility as ViewIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import CustomTablePagination from '../../../components/CustomTablePagination';
import PlanFormDialog, { PlanOwner } from '../../../components/plans/PlanFormDialog';
import PlanViewerDialog from '../../../components/plans/PlanViewerDialog';
import { PlanKindChip, planMeta } from '../../../components/plans/planUi';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useBureauEtudes } from '../../../hooks/queries/useBureauEtudes';
import { plansKeys, usePlanMachines, usePlansTechniques } from '../../../hooks/queries/usePlansTechniques';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { ProjetDessin } from '../../../types/projetDessin';

const PROD_COLOR = '#FF5722';
const fD = (d?: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const GROUPES = [
  { value: '', label: 'Tous', key: 'tous' },
  { value: 'pliage', label: 'Pliage', key: 'pliage' },
  { value: 'dxf', label: 'DXF · découpe', key: 'dxf' },
  { value: 'autres', label: 'Autres', key: 'autres' },
] as const;

/** Production › Plans techniques › « Tous les plans » (prototype #plans). */
const TousLesPlans: React.FC<{ onEdit: (p: ProjetDessin) => void }> = ({ onEdit }) => {
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission('Production', 'add', 'Dessins');
  const canSeeBE = hasPermission('Production', 'view', "Bureau d'études");
  const [groupe, setGroupe] = useState('');
  const [machine, setMachine] = useState<number | ''>('');
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(25);
  const [vue, setVue] = useState<ProjetDessin | null>(null);
  const [depot, setDepot] = useState<PlanOwner | null>(null);
  useEffect(() => { const t = setTimeout(() => { setQ(text.trim()); setPage(0); }, 300); return () => clearTimeout(t); }, [text]);

  const { data, isFetching } = usePlansTechniques({ groupe, machine_id: machine, q, skip: page * rows, limit: rows });
  const { data: machines } = usePlanMachines();
  const { data: be } = useBureauEtudes();
  const sansPlan = canSeeBE && be ? [...be.attente, ...be.conception].filter((c) => c.plans === 0) : [];

  return (
    <Box>
      {sansPlan.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {sansPlan.length} travail(s) au bureau d'études sans plan déposé
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {sansPlan.map((c) => (
              <Chip key={c.id} size="small" variant="outlined" label={`${c.code} · ${c.nom}`} title={canAdd ? 'Déposer un plan' : undefined}
                icon={canAdd ? <UploadIcon /> : undefined}
                onClick={canAdd ? () => setDepot({ type: c.type, id: c.entity_id, code: c.code, nom: c.nom }) : undefined} />
            ))}
          </Stack>
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5} sx={{ px: 2, pt: 1 }}>
          <Tabs value={groupe} onChange={(_, v) => { setGroupe(v); setPage(0); }} sx={{ flex: 1, '& .Mui-selected': { color: `${PROD_COLOR} !important` } }}
            TabIndicatorProps={{ sx: { bgcolor: PROD_COLOR } }}>
            {GROUPES.map((g) => <Tab key={g.key} value={g.value} label={`${g.label} (${data?.counts[g.key] ?? 0})`} />)}
          </Tabs>
          <TextField size="small" placeholder="Référence, pièce, client, fichier…" value={text} onChange={(e) => setText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ minWidth: 260 }} />
          <TextField select size="small" label="Machine" value={machine} onChange={(e) => { setMachine(e.target.value === '' ? '' : Number(e.target.value)); setPage(0); }}
            sx={{ minWidth: 200 }} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value="">Toutes les machines</MenuItem>
            {(machines || []).map((m) => <MenuItem key={m.id} value={m.id}>{m.code} — {m.name}</MenuItem>)}
          </TextField>
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={getModuleTableTheme('production')}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Référence</TableCell><TableCell>Pièce</TableCell><TableCell>Projet / service</TableCell><TableCell>Type</TableCell>
              <TableCell>Machine</TableCell><TableCell>Reçu par (étape · opérateur)</TableCell><TableCell>Fichier</TableCell><TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {!data && isFetching ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}><CircularProgress sx={{ color: PROD_COLOR }} /></TableCell></TableRow>
            ) : !data?.items.length ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                {q || machine || groupe ? 'Aucun plan ne correspond.' : 'Aucun plan technique déposé pour l\'instant.'}
              </TableCell></TableRow>
            ) : data.items.map((p) => {
              const v = p.latest_version;
              return (
                <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => setVue(p)}>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{p.piece_ref || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.title}</Typography>
                    {planMeta(p) && <Typography variant="caption" color="text.secondary">{planMeta(p)}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2"><b>{p.owner?.code}</b> · {p.owner?.nom}</Typography>
                    {p.owner?.client && <Typography variant="caption" color="text.secondary">{p.owner.client}</Typography>}
                  </TableCell>
                  <TableCell><PlanKindChip kind={p.kind} /></TableCell>
                  <TableCell>
                    {p.machine ? <><Typography variant="body2">{p.machine.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{p.machine.code}</Typography></> : '—'}
                  </TableCell>
                  <TableCell>
                    {(p.destinataires || []).length
                      ? (p.destinataires || []).map((d) => (
                        <Typography key={`${d.type}-${d.id}`} variant="caption" display="block">
                          {d.label}{d.machine ? ` · ${d.machine}` : ''} · {d.responsable || 'à désigner'}
                        </Typography>
                      ))
                      : <Typography variant="caption" color="text.secondary">Pas encore de tâche</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v?.file_name}</Typography>
                    <Typography variant="caption" color="text.secondary">v{v?.version || 1} · {fD(v?.uploaded_at)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<ViewIcon />} onClick={(e) => { e.stopPropagation(); setVue(p); }}>Consulter</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <CustomTablePagination count={data?.total || 0} page={page} rowsPerPage={rows}
          onPageChange={(_, n) => setPage(n)} onRowsPerPageChange={(e) => { setRows(parseInt(e.target.value, 10)); setPage(0); }} />
      </TableContainer>

      <PlanViewerDialog plan={vue} onClose={() => setVue(null)}
        onEdit={hasPermission('Production', 'edit', 'Dessins') ? (p) => { setVue(null); onEdit(p); } : undefined} />
      <PlanFormDialog open={!!depot} owner={depot} onClose={() => setDepot(null)}
        onSaved={() => { setDepot(null); qc.invalidateQueries({ queryKey: plansKeys.root }); qc.invalidateQueries({ queryKey: ['production'] }); }} />
    </Box>
  );
};

export default TousLesPlans;
